import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  FormControlLabel,
  Checkbox,
  Grid,
  Tabs,
  Tab,
  IconButton,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { Add, Delete, Download, UploadFile } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import axios from 'axios';
import { useToast } from '../../contexts/ToastContext';
import { normalizePartNumber } from '../../utils/partNumber';
import { displayDateTimeToUTC, utcToDisplayDateAndTime, getTimezoneLabel } from '../../utils/timezoneUtils';
import { downloadAuthenticatedPdf } from '../../utils/authenticatedPdf';

dayjs.extend(utc);
dayjs.extend(timezone);

/** Combine a calendar date with hours/minutes from a time value. */
const combineDateAndTime = (dateValue, timeValue, defaultHour = 0, defaultMinute = 0) => {
  if (!dateValue) return null;
  const base = dayjs(dateValue);
  if (!base.isValid()) return null;
  const t = timeValue ? dayjs(timeValue) : null;
  return base
    .hour(t && t.isValid() ? t.hour() : defaultHour)
    .minute(t && t.isValid() ? t.minute() : defaultMinute)
    .second(0)
    .millisecond(0);
};

const isSameCalendarDay = (left, right) => {
  if (!left || !right) return false;
  return dayjs(left).format('YYYY-MM-DD') === dayjs(right).format('YYYY-MM-DD');
};

/**
 * Build [start, end] for a time window from start/end dates + times.
 * Same calendar day → times bound that day; different days → from startDate@startTime through endDate@endTime.
 */
const buildWindowFromParts = ({ startDate, endDate, startTime, endTime }) => {
  if (!startDate) return { error: 'Start date is required for a time window schedule' };
  const resolvedEndDate = endDate || startDate;
  if (dayjs(resolvedEndDate).startOf('day').isBefore(dayjs(startDate).startOf('day'))) {
    return { error: 'End date cannot be before start date' };
  }
  const start = combineDateAndTime(startDate, startTime, 6, 0);
  let end = combineDateAndTime(resolvedEndDate, endTime, 8, 0);
  if (!start || !end) return { error: 'Invalid schedule date/time' };
  if (!end.isAfter(start)) {
    if (isSameCalendarDay(start, resolvedEndDate)) {
      end = start.add(2, 'hour');
    } else {
      return { error: 'Schedule end must be after schedule start' };
    }
  }
  return { start, end, sameDay: isSameCalendarDay(start, end) };
};

const PlanningActions = ({ open, onClose, workOrder, onSuccess, displayTimezone = 'Asia/Kolkata' }) => {
  const [tab, setTab] = useState(0);
  const [scheduledDate, setScheduledDate] = useState(null);
  const [scheduledEndDate, setScheduledEndDate] = useState(null);
  const [scheduleMode, setScheduleMode] = useState('FULL_DAY');
  const [needsStoreAction, setNeedsStoreAction] = useState(false);
  const [needsMROAction, setNeedsMROAction] = useState(false);
  const [needsExternalAgencyAction, setNeedsExternalAgencyAction] = useState(false);
  const [externalAgencyId, setExternalAgencyId] = useState('');
  const [externalAgencies, setExternalAgencies] = useState([]);
  const [notes, setNotes] = useState('');
  const [decision, setDecision] = useState('APPROVE');
  const [remarks, setRemarks] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isHangarSku, setIsHangarSku] = useState(false);
  const [operators, setOperators] = useState([]);
  const [operatorId, setOperatorId] = useState('');
  const isExternalHangarJob = ['EXTERNAL_API', 'EXTERNAL_MANUAL', 'EXTERNAL_PORTAL'].includes(workOrder?.jobSource);
  const isPortalClientRequest = workOrder?.jobSource === 'EXTERNAL_PORTAL' && !workOrder?.planningApproved;
  const requireHangarOperator = isHangarSku && isExternalHangarJob;
  const needsScheduleUpdate =
    Boolean(workOrder?.planningApproved) &&
    (
      (workOrder?.needsStoreAction && workOrder?.storeRejectedAt && !workOrder?.storeAccepted) ||
      (workOrder?.needsMROAction && workOrder?.mroRejectedAt && !workOrder?.mroAccepted)
    );
  const approveLabel = isPortalClientRequest
    ? 'Create work order'
    : (needsScheduleUpdate ? 'Update schedule' : 'Approve');
  
  // Parts Procurement Requirements
  const [partNumbers, setPartNumbers] = useState(['']);
  const [quantities, setQuantities] = useState(['']);
  const [descriptions, setDescriptions] = useState(['']);
  const [specifications, setSpecifications] = useState('');
  const [procurementLoading, setProcurementLoading] = useState(false);
  const [partCatalog, setPartCatalog] = useState([]);
  const [partsLoading, setPartsLoading] = useState(false);
  const [inventoryAvailabilityMap, setInventoryAvailabilityMap] = useState({});
  const [inventoryAvailabilityLoading, setInventoryAvailabilityLoading] = useState(false);
  const csvUploadInputRef = useRef(null);
  const [attachments, setAttachments] = useState([]);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [deletingAttachment, setDeletingAttachment] = useState('');
  
  const { showToast } = useToast();

  useEffect(() => {
    if (workOrder) {
      setAttachments(Array.isArray(workOrder.attachments) ? workOrder.attachments : []);
      const mode = workOrder.scheduleMode === 'HOURLY' ? 'HOURLY' : 'FULL_DAY';
      setScheduleMode(mode);
      if (workOrder.scheduledDate) {
        if (displayTimezone) {
          const { date: d, time: t } = utcToDisplayDateAndTime(workOrder.scheduledDate, displayTimezone);
          setScheduledDate(d ? dayjs.tz(`${d}T${t || '00:00'}`, displayTimezone) : dayjs(workOrder.scheduledDate));
        } else {
          setScheduledDate(dayjs(workOrder.scheduledDate));
        }
      } else if (workOrder.dueDate) {
        if (displayTimezone) {
          const { date: d } = utcToDisplayDateAndTime(workOrder.dueDate, displayTimezone);
          setScheduledDate(d ? dayjs.tz(`${d}T00:00`, displayTimezone) : dayjs(workOrder.dueDate));
        } else {
          setScheduledDate(dayjs(workOrder.dueDate));
        }
      } else {
        setScheduledDate(null);
      }
      if (mode === 'HOURLY' && workOrder.scheduledEndDate) {
        if (displayTimezone) {
          const { date: d, time: t } = utcToDisplayDateAndTime(workOrder.scheduledEndDate, displayTimezone);
          setScheduledEndDate(d ? dayjs.tz(`${d}T${t || '00:00'}`, displayTimezone) : dayjs(workOrder.scheduledEndDate));
        } else {
          setScheduledEndDate(dayjs(workOrder.scheduledEndDate));
        }
      } else {
        setScheduledEndDate(null);
      }
      setNeedsStoreAction(workOrder.needsStoreAction || false);
      setNeedsMROAction(workOrder.needsMROAction || false);
      setNeedsExternalAgencyAction(workOrder.needsExternalAgencyAction || false);
      setExternalAgencyId(String(workOrder.externalAgencyId?._id || workOrder.externalAgencyId || ''));
      setNotes(workOrder.planningNotes || '');
      setDecision('APPROVE');
      setRemarks('');
      setOperatorId(String(workOrder.operatorId?._id || workOrder.operatorId || ''));
      axios.get('/api/master-data/agencies', {
        params: { isActive: 'true', type: 'EXTERNAL_MAINTENANCE' }
      }).then((res) => {
        const list = Array.isArray(res.data) ? res.data : (res.data?.items || []);
        setExternalAgencies(list);
      }).catch(() => setExternalAgencies([]));
      
      // Load procurement requirements if they exist
      if (workOrder.partsProcurementRequirements?.partNumbers) {
        const existingPartNumbers = workOrder.partsProcurementRequirements.partNumbers || [];
        const existingQuantities = workOrder.partsProcurementRequirements.quantities || [];
        const existingDescriptions = workOrder.partsProcurementRequirements.descriptions || [];
        setPartNumbers([...existingPartNumbers, '']);
        setQuantities(
          existingPartNumbers.map((_, idx) => existingQuantities[idx] ?? '').concat([''])
        );
        setDescriptions(
          existingPartNumbers.map((_, idx) => existingDescriptions[idx] || '').concat([''])
        );
        setSpecifications(workOrder.partsProcurementRequirements.specifications || '');
      } else {
        const fromComponents = [];
        (workOrder.requiredSpares || []).forEach((line) => {
          const pn = normalizePartNumber(line.partNumber);
          if (!pn) return;
          fromComponents.push({
            partNumber: pn,
            quantity: Number(line.quantity) || 1,
            description: String(line.itemName || '').trim()
          });
        });
        (workOrder.requiredConsumables || []).forEach((line) => {
          const pn = normalizePartNumber(line.partNumberOrSpecification);
          if (!pn) return;
          fromComponents.push({
            partNumber: pn,
            quantity: Number(line.quantity) || 1,
            description: String(line.itemName || '').trim()
          });
        });
        if (fromComponents.length > 0) {
          setPartNumbers(fromComponents.map((row) => row.partNumber).concat(['']));
          setQuantities(fromComponents.map((row) => row.quantity).concat(['']));
          setDescriptions(fromComponents.map((row) => row.description).concat(['']));
        } else {
          setPartNumbers(['']);
          setQuantities(['']);
          setDescriptions(['']);
        }
        setSpecifications('');
      }
    }
  }, [workOrder]);

  useEffect(() => {
    if (!open) return;
    const loadParts = async () => {
      setPartsLoading(true);
      try {
        const res = await axios.get('/api/parts', { params: { isActive: 'true', page: 1, pageSize: 500 } });
        const parts = Array.isArray(res.data?.parts) ? res.data.parts : (Array.isArray(res.data) ? res.data : []);
        setPartCatalog(parts);
      } catch (err) {
        console.error('Failed to load parts catalog', err);
      } finally {
        setPartsLoading(false);
      }
    };
    loadParts();

    const loadHangarAndOperators = async () => {
      try {
        const settingsRes = await axios.get('/api/mro/hangar-settings').catch(() => ({ data: {} }));
        const hangarMode = settingsRes.data?.productMode === 'HANGAR';
        setIsHangarSku(hangarMode);
        if (!hangarMode) {
          setOperators([]);
          return;
        }
        const opRes = await axios.get('/api/master-data/operators', { params: { isActive: 'true' } });
        setOperators(Array.isArray(opRes.data) ? opRes.data : []);
      } catch (err) {
        console.error('Failed to load hangar operators for planning', err);
        setIsHangarSku(false);
        setOperators([]);
      }
    };
    loadHangarAndOperators();
  }, [open]);

  const partDescriptionMap = useMemo(() => {
    const map = {};
    (partCatalog || []).forEach((part) => {
      const key = String(part?.partNumber || '').trim().toLowerCase();
      if (!key || map[key]) return;
      map[key] = String(part?.description || part?.partName || '').trim();
    });
    return map;
  }, [partCatalog]);

  const normalizedEnteredPartNumbers = useMemo(() => (
    Array.from(
      new Set(
        partNumbers
          .map((pn) => normalizePartNumber(pn).toLowerCase())
          .filter(Boolean)
      )
    )
  ), [partNumbers]);

  useEffect(() => {
    if (!open) return;
    if (!normalizedEnteredPartNumbers.length) {
      setInventoryAvailabilityMap({});
      return;
    }

    let cancelled = false;
    const loadInventoryAvailability = async () => {
      setInventoryAvailabilityLoading(true);
      try {
        const responses = await Promise.all(
          normalizedEnteredPartNumbers.map((partNumber) => (
            axios.get('/api/store/inventory', {
              params: {
                status: 'IN_INVENTORY',
                search: partNumber,
                page: 1,
                pageSize: 500
              }
            })
          ))
        );

        if (cancelled) return;

        const liveAvailabilityMap = {};
        responses.forEach((response, index) => {
          const queryPartNumber = normalizedEnteredPartNumbers[index];
          const items = Array.isArray(response.data?.items)
            ? response.data.items
            : (Array.isArray(response.data) ? response.data : []);

          const exactMatchTotal = items.reduce((sum, item) => {
            const itemPartNumber = normalizePartNumber(item?.partNumber).toLowerCase();
            if (!itemPartNumber || itemPartNumber !== queryPartNumber) return sum;
            return sum + (Number(item?.quantity) || 0);
          }, 0);

          liveAvailabilityMap[queryPartNumber] = exactMatchTotal;
        });

        setInventoryAvailabilityMap(liveAvailabilityMap);
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load live inventory availability', err);
          setInventoryAvailabilityMap({});
        }
      } finally {
        if (!cancelled) setInventoryAvailabilityLoading(false);
      }
    };

    loadInventoryAvailability();
    return () => {
      cancelled = true;
    };
  }, [open, normalizedEnteredPartNumbers]);

  const handleAddPartNumber = () => {
    setPartNumbers([...partNumbers, '']);
    setQuantities([...quantities, '']);
    setDescriptions([...descriptions, '']);
  };

  const handleRemovePartNumber = (index) => {
    const newPartNumbers = partNumbers.filter((_, i) => i !== index);
    const newQuantities = quantities.filter((_, i) => i !== index);
    const newDescriptions = descriptions.filter((_, i) => i !== index);
    setPartNumbers(newPartNumbers.length > 0 ? newPartNumbers : ['']);
    setQuantities(newQuantities.length > 0 ? newQuantities : ['']);
    setDescriptions(newDescriptions.length > 0 ? newDescriptions : ['']);
  };

  const handlePartNumberChange = (index, value) => {
    const newPartNumbers = [...partNumbers];
    newPartNumbers[index] = normalizePartNumber(value);
    setPartNumbers(newPartNumbers);
    const normalized = String(value || '').trim().toLowerCase();
    const matchedDescription = normalized ? (partDescriptionMap[normalized] || '') : '';
    setDescriptions((prev) => {
      const next = [...prev];
      const current = String(next[index] || '').trim();
      if (!current || matchedDescription) {
        next[index] = matchedDescription;
      }
      return next;
    });
  };

  const handleQuantityChange = (index, value) => {
    const newQuantities = [...quantities];
    newQuantities[index] = value;
    setQuantities(newQuantities);
  };

  const handleDescriptionChange = (index, value) => {
    const next = [...descriptions];
    next[index] = value;
    setDescriptions(next);
  };

  const escapeCsv = (value) => {
    const stringValue = String(value ?? '');
    if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  };

  const parseCsvLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i += 1) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i += 1;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (ch === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += ch;
      }
    }
    result.push(current.trim());
    return result;
  };

  const handleDownloadProcurementCsv = () => {
    const rows = partNumbers
      .map((partNumber, idx) => ({
        partNumber: String(partNumber || '').trim(),
        quantity: Number(quantities[idx]) || '',
        description: String(descriptions[idx] || '').trim()
      }))
      .filter((row) => row.partNumber);

    const csvLines = [
      ['Part Number', 'Quantity', 'Description'].join(','),
      ...rows.map((row) => [
        escapeCsv(row.partNumber),
        escapeCsv(row.quantity),
        escapeCsv(row.description)
      ].join(','))
    ];
    const blob = new Blob([csvLines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `procurement-requirements-${workOrder?.workOrderNumber || workOrder?._id || 'work-order'}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  const handleUploadCsvClick = () => {
    csvUploadInputRef.current?.click();
  };

  const handleUploadProcurementCsv = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const lines = text.split(/\r?\n/).filter((line) => line.trim());
      if (lines.length < 2) {
        showToast('CSV has no data rows to import', 'error');
        return;
      }
      const header = parseCsvLine(lines[0]).map((h) => h.toLowerCase().trim());
      const pnIdx = header.findIndex((h) => h === 'part number' || h === 'partnumber' || h === 'pn');
      const qtyIdx = header.findIndex((h) => h === 'quantity' || h === 'qty');
      const descIdx = header.findIndex((h) => h === 'description' || h === 'desc');
      if (pnIdx < 0 || qtyIdx < 0) {
        showToast('CSV must contain "Part Number" and "Quantity" columns', 'error');
        return;
      }
      const importedRows = lines.slice(1).map((line) => parseCsvLine(line)).map((columns) => {
        const partNumber = normalizePartNumber(columns[pnIdx]);
        const qtyRaw = Number(columns[qtyIdx]);
        const quantity = Number.isFinite(qtyRaw) && qtyRaw > 0 ? qtyRaw : 1;
        const uploadedDescription = descIdx >= 0 ? String(columns[descIdx] || '').trim() : '';
        const knownDescription = partDescriptionMap[partNumber.toLowerCase()] || '';
        return {
          partNumber,
          quantity,
          description: knownDescription || uploadedDescription
        };
      }).filter((row) => row.partNumber);

      if (!importedRows.length) {
        showToast('No valid part rows found in CSV', 'error');
        return;
      }

      setPartNumbers(importedRows.map((row) => row.partNumber).concat(['']));
      setQuantities(importedRows.map((row) => row.quantity).concat(['']));
      setDescriptions(importedRows.map((row) => row.description).concat(['']));
      showToast(`Imported ${importedRows.length} procurement row(s)`, 'success');
    } catch (err) {
      console.error(err);
      showToast('Failed to import CSV', 'error');
    } finally {
      event.target.value = '';
    }
  };

  const handleProvideProcurementRequirements = async () => {
    try {
      setProcurementLoading(true);
      setError('');

      const validRows = partNumbers
        .map((partNumber, index) => ({
          partNumber: normalizePartNumber(partNumber),
          quantity: Number(quantities[index]) || 1,
          description: String(descriptions[index] || '').trim()
        }))
        .filter((row) => row.partNumber);
      const validPartNumbers = validRows.map((row) => row.partNumber);
      const validQuantities = validRows.map((row) => row.quantity);
      const validDescriptions = validRows.map((row) => row.description);

      if (validPartNumbers.length === 0) {
        setError('At least one part number is required');
        return;
      }

      await axios.put(`/api/maintenance/${workOrder._id}/planning/provide-procurement-requirements`, {
        partNumbers: validPartNumbers,
        quantities: validQuantities.length > 0 ? validQuantities.map(q => Number(q) || 1) : undefined,
        descriptions: validDescriptions.length > 0 ? validDescriptions.map((d) => String(d || '').trim()) : undefined,
        specifications: specifications.trim() || undefined
      });

      showToast('Parts procurement requirements provided successfully', 'success');
      onSuccess();
      // Don't close dialog, just switch to approval tab
      setTab(0);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to provide procurement requirements';
      setError(errorMessage);
      showToast(errorMessage, 'error');
    } finally {
      setProcurementLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');

      if (decision === 'APPROVE' && !String(workOrder?.title || '').trim()) {
        setError('Work order title is required before planning approval. Update the work order title in Maintenance, then return here.');
        return;
      }
      if (decision === 'APPROVE' && requireHangarOperator && !operatorId) {
        setError('Operator is required before planning approval for hangar external jobs.');
        return;
      }
      if (decision === 'APPROVE' && !needsStoreAction && !needsMROAction && !needsExternalAgencyAction) {
        setError('At least one action (Store, MRO, or External Agency) must be selected');
        return;
      }
      if (decision === 'APPROVE' && needsExternalAgencyAction && !externalAgencyId) {
        setError('Select an External Agency when Needs External Agency Action is checked');
        return;
      }
      if (decision === 'REJECT' && !remarks.trim()) {
        setError('Rejection remarks are required');
        return;
      }
      if (decision === 'APPROVE' && workOrder?.storeRejectedAt && workOrder?.storeAvailabilityDate) {
        const selectedDate = scheduledDate ? dayjs(scheduledDate).startOf('day') : null;
        const storeAvailableFrom = dayjs(workOrder.storeAvailabilityDate).startOf('day');
        if (!selectedDate || !selectedDate.isAfter(storeAvailableFrom)) {
          setError(`Scheduled date must be after Store availability date (${storeAvailableFrom.format('YYYY-MM-DD')}).`);
          return;
        }
      }

      let scheduledDateISO;
      let scheduledEndDateISO;
      if (scheduleMode === 'HOURLY') {
        const built = buildWindowFromParts({
          startDate: scheduledDate,
          endDate: scheduledEndDate || scheduledDate,
          startTime: scheduledDate,
          endTime: scheduledEndDate
        });
        if (built.error) {
          setError(built.error);
          return;
        }
        if (isSameCalendarDay(built.start, built.end) && !built.end.isAfter(built.start)) {
          setError('On the same day, end time must be after start time');
          return;
        }
        scheduledDateISO = displayTimezone
          ? (displayDateTimeToUTC(
            built.start.format('YYYY-MM-DD'),
            built.start.format('HH:mm'),
            displayTimezone
          ) ?? built.start.toISOString())
          : built.start.toISOString();
        scheduledEndDateISO = displayTimezone
          ? (displayDateTimeToUTC(
            built.end.format('YYYY-MM-DD'),
            built.end.format('HH:mm'),
            displayTimezone
          ) ?? built.end.toISOString())
          : built.end.toISOString();
      } else if (scheduledDate) {
        scheduledDateISO = displayTimezone
          ? (displayDateTimeToUTC(dayjs(scheduledDate).format('YYYY-MM-DD'), '00:00', displayTimezone) ?? dayjs(scheduledDate).startOf('day').toISOString())
          : dayjs(scheduledDate).startOf('day').toISOString();
        scheduledEndDateISO = null;
      }

      await axios.put(`/api/maintenance/${workOrder._id}/planning/approve`, {
        decision,
        scheduledDate: scheduledDateISO,
        scheduledEndDate: scheduleMode === 'HOURLY' ? scheduledEndDateISO : null,
        scheduleMode,
        needsStoreAction,
        needsMROAction,
        needsExternalAgencyAction,
        externalAgencyId: needsExternalAgencyAction ? externalAgencyId : null,
        notes: notes.trim() || undefined,
        remarks: remarks.trim() || undefined,
        ...(requireHangarOperator && operatorId ? { operatorId } : {})
      });

      showToast(
        decision === 'REJECT'
          ? 'Planning rejected successfully'
          : (isPortalClientRequest
            ? 'Work order created from client request'
            : (needsScheduleUpdate
              ? 'Scheduled date updated successfully'
              : 'Planning approval completed successfully')),
        'success'
      );
      onSuccess();
      handleClose();
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to approve work order';
      setError(errorMessage);
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };


  const handleClose = () => {
    setTab(0);
    setScheduledDate(null);
    setScheduledEndDate(null);
    setScheduleMode('FULL_DAY');
    setNeedsStoreAction(false);
    setNeedsMROAction(false);
    setDecision('APPROVE');
    setRemarks('');
    setNotes('');
    setOperatorId('');
    setPartNumbers(['']);
    setQuantities(['']);
    setDescriptions(['']);
    setSpecifications('');
    setError('');
    onClose();
  };

  const availabilityByPartNumber = useMemo(() => {
    const map = {};
    const toKey = (value) => normalizePartNumber(String(value || '')).toLowerCase();

    // Use latest saved procurement snapshot when available.
    const reqPartNumbers = workOrder?.partsProcurementRequirements?.partNumbers || [];
    const reqAvailable = workOrder?.partsProcurementRequirements?.availableQuantities || [];
    reqPartNumbers.forEach((pn, index) => {
      const key = toKey(pn);
      if (!key) return;
      map[key] = Math.max(Number(reqAvailable[index]) || 0, map[key] || 0);
    });

    // Use maintenance endpoint computed availability for requested parts.
    (workOrder?.partsAvailability || []).forEach((item) => {
      const key = toKey(item?.partNumber);
      if (!key) return;
      map[key] = Math.max(Number(item?.available) || 0, map[key] || 0);
    });

    // Backward compatibility for older payload shapes.
    (workOrder?.partsRequired || []).forEach((item) => {
      const key = toKey(item?.partNumber);
      if (!key) return;
      map[key] = Math.max(Number(item?.available) || 0, map[key] || 0);
    });

    // Prefer live inventory lookup for Planning-time visibility.
    Object.entries(inventoryAvailabilityMap || {}).forEach(([key, qty]) => {
      if (!key) return;
      map[key] = Math.max(Number(qty) || 0, map[key] || 0);
    });

    return map;
  }, [inventoryAvailabilityMap, workOrder]);

  const procurementPreviewRows = partNumbers
    .map((partNumber, index) => {
      const normalizedPart = normalizePartNumber(partNumber);
      const trimmedPart = String(normalizedPart || '').trim();
      const requiredQty = Number(quantities[index]) || 0;
      if (!trimmedPart || requiredQty <= 0) return null;

      const partKey = trimmedPart.toLowerCase();
      const availableQty = Number(availabilityByPartNumber[partKey]) || 0;
      const remainingQty = Math.max(requiredQty - availableQty, 0);

      return {
        partNumber: trimmedPart,
        requiredQty,
        availableQty,
        remainingQty
      };
    })
    .filter(Boolean);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{needsScheduleUpdate ? 'Update Scheduled Date' : 'Planning Actions'}</DialogTitle>
        <DialogContent>
          <Tabs value={tab} onChange={(e, newValue) => setTab(newValue)} sx={{ mb: 2 }}>
            <Tab label={needsScheduleUpdate ? 'Schedule' : 'Approval'} />
            <Tab label="Procurement Requirements" />
          </Tabs>

          {tab === 0 && (
            <Box>
          {workOrder?.storeRejectedAt && workOrder?.storeAvailabilityDate && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Store set an unavailable-parts availability date. Planning must schedule maintenance after: {dayjs(workOrder.storeAvailabilityDate).format('YYYY-MM-DD')}.
            </Alert>
          )}
          {needsScheduleUpdate && workOrder?.mroRejectedAt && !workOrder?.storeRejectedAt && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              MRO rejected this work order. Update the scheduled date (and notes) before sending it forward again.
            </Alert>
          )}
          {!String(workOrder?.title || '').trim() && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Work order title is missing. CAMO must set the title before planning can approve this work order.
            </Alert>
          )}
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Work Order: <strong>{String(workOrder?.title || '').trim() || '— title required —'}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Aircraft: <strong>{workOrder?.aircraftId?.registration || 'Unknown'}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Due Date (set by CAMO at WO creation): <strong>{workOrder?.dueDate ? dayjs(workOrder.dueDate).format('YYYY-MM-DD') : (workOrder?.scheduledDate ? dayjs(workOrder.scheduledDate).format('YYYY-MM-DD') : 'N/A')}</strong>
            </Typography>
          </Box>

          <Box sx={{ mb: 2, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Attachments
            </Typography>
            {attachments.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                No attachments yet.
              </Typography>
            ) : (
              <Box sx={{ mb: 1 }}>
                {attachments.map((att) => (
                  <Box
                    key={att.fileName}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}
                  >
                    <Typography variant="body2" sx={{ flex: 1, minWidth: 120 }} noWrap>
                      {att.originalFileName || att.fileName}
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<Download fontSize="small" />}
                      onClick={async () => {
                        try {
                          await downloadAuthenticatedPdf(
                            `/api/mro/packages/${workOrder._id}/attachments/${encodeURIComponent(att.fileName)}?download=1`,
                            att.originalFileName || att.fileName
                          );
                        } catch (err) {
                          showToast(err.response?.data?.message || 'Download failed', 'error');
                        }
                      }}
                    >
                      Download
                    </Button>
                    <IconButton
                      size="small"
                      color="error"
                      aria-label="Remove attachment"
                      disabled={deletingAttachment === att.fileName || loading}
                      onClick={async () => {
                        setDeletingAttachment(att.fileName);
                        try {
                          const res = await axios.delete(
                            `/api/mro/packages/${workOrder._id}/attachments/${encodeURIComponent(att.fileName)}`
                          );
                          setAttachments(res.data?.attachments || []);
                          showToast('Attachment removed', 'success');
                          if (onSuccess) onSuccess();
                        } catch (err) {
                          showToast(err.response?.data?.message || 'Failed to remove attachment', 'error');
                        } finally {
                          setDeletingAttachment('');
                        }
                      }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
            <Button
              variant="outlined"
              size="small"
              component="label"
              startIcon={<UploadFile />}
              disabled={uploadingAttachment || loading || !workOrder?._id}
            >
              {uploadingAttachment ? 'Uploading…' : 'Upload attachment'}
              <input
                type="file"
                hidden
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.txt,.csv,.zip"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  e.target.value = '';
                  if (!file || !workOrder?._id) return;
                  setUploadingAttachment(true);
                  try {
                    const fd = new FormData();
                    fd.append('file', file);
                    const res = await axios.post(
                      `/api/mro/packages/${workOrder._id}/attachments`,
                      fd,
                      { headers: { 'Content-Type': 'multipart/form-data' } }
                    );
                    const next = res.data?.package?.attachments
                      || [...attachments, res.data?.attachment].filter(Boolean);
                    setAttachments(next);
                    showToast('Attachment uploaded', 'success');
                    if (onSuccess) onSuccess();
                  } catch (err) {
                    showToast(err.response?.data?.message || 'Upload failed', 'error');
                  } finally {
                    setUploadingAttachment(false);
                  }
                }}
              />
            </Button>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Typography variant="body2" gutterBottom>Decision</Typography>
              <ToggleButtonGroup
                value={decision}
                exclusive
                onChange={(e, v) => v && setDecision(v)}
                fullWidth
                size="small"
              >
                <ToggleButton value="APPROVE">{approveLabel}</ToggleButton>
                <ToggleButton value="REJECT" color="error">Reject</ToggleButton>
              </ToggleButtonGroup>
              {isPortalClientRequest && (
                <Alert severity="info" sx={{ mt: 1.5 }}>
                  This is a client portal request. Confirm schedule, Store/MRO flags, and Operator,
                  then create the work order (WO / MRO numbers are assigned at this step).
                </Alert>
              )}
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" gutterBottom>
                Schedule type
              </Typography>
              <ToggleButtonGroup
                value={scheduleMode}
                exclusive
                onChange={(e, v) => {
                  if (!v) return;
                  setScheduleMode(v);
                  if (v === 'FULL_DAY') {
                    setScheduledEndDate(null);
                  } else if (scheduledDate && !scheduledEndDate) {
                    const start = combineDateAndTime(scheduledDate, scheduledDate, 6, 0) || dayjs(scheduledDate).hour(6).minute(0);
                    setScheduledDate(start);
                    setScheduledEndDate(start.add(2, 'hour'));
                  }
                }}
                fullWidth
                size="small"
              >
                <ToggleButton value="FULL_DAY">Full day</ToggleButton>
                <ToggleButton value="HOURLY">Time window</ToggleButton>
              </ToggleButtonGroup>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                Full day blocks one calendar day. Time window: same start/end date uses times that day; different dates span from start date/time through end date/time.
              </Typography>
            </Grid>
            {scheduleMode === 'HOURLY' ? (
              <>
                <Grid item xs={12} md={6}>
                  <DatePicker
                    label={displayTimezone ? `Start date (${getTimezoneLabel(displayTimezone)})` : 'Start date'}
                    value={scheduledDate}
                    onChange={(date) => {
                      if (!date) {
                        setScheduledDate(null);
                        return;
                      }
                      const nextStart = combineDateAndTime(date, scheduledDate, 6, 0);
                      const endWasSameDay = !scheduledEndDate || isSameCalendarDay(scheduledDate, scheduledEndDate);
                      let nextEnd;
                      if (endWasSameDay) {
                        nextEnd = combineDateAndTime(
                          date,
                          scheduledEndDate || (nextStart ? nextStart.add(2, 'hour') : null),
                          8,
                          0
                        );
                        if (nextStart && nextEnd && !nextEnd.isAfter(nextStart)) {
                          nextEnd = nextStart.add(2, 'hour');
                        }
                      } else {
                        nextEnd = combineDateAndTime(scheduledEndDate, scheduledEndDate, 8, 0);
                        if (nextStart && nextEnd && dayjs(nextEnd).startOf('day').isBefore(dayjs(nextStart).startOf('day'))) {
                          nextEnd = combineDateAndTime(date, scheduledEndDate, 8, 0);
                          if (nextEnd && !nextEnd.isAfter(nextStart)) nextEnd = nextStart.add(2, 'hour');
                        }
                      }
                      setScheduledDate(nextStart);
                      setScheduledEndDate(nextEnd);
                    }}
                    slotProps={{ textField: { fullWidth: true, required: true } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DatePicker
                    label={displayTimezone ? `End date (${getTimezoneLabel(displayTimezone)})` : 'End date'}
                    value={scheduledEndDate || scheduledDate}
                    minDate={scheduledDate || undefined}
                    onChange={(date) => {
                      if (!date) return;
                      if (scheduledDate && dayjs(date).startOf('day').isBefore(dayjs(scheduledDate).startOf('day'))) {
                        setError('End date cannot be before start date');
                        return;
                      }
                      setError('');
                      const nextEnd = combineDateAndTime(date, scheduledEndDate || scheduledDate, 8, 0);
                      if (scheduledDate && nextEnd && isSameCalendarDay(scheduledDate, date) && !nextEnd.isAfter(dayjs(scheduledDate))) {
                        setScheduledEndDate(dayjs(scheduledDate).add(2, 'hour'));
                        return;
                      }
                      setScheduledEndDate(nextEnd);
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        helperText: !scheduledDate ? 'Select start date first' : undefined
                      }
                    }}
                    disabled={!scheduledDate}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TimePicker
                    label={isSameCalendarDay(scheduledDate, scheduledEndDate || scheduledDate) ? 'Start time (same day)' : 'Start time'}
                    value={scheduledDate}
                    onChange={(time) => {
                      const nextStart = combineDateAndTime(scheduledDate || dayjs(), time, 6, 0);
                      setScheduledDate(nextStart);
                      if (
                        nextStart &&
                        scheduledEndDate &&
                        isSameCalendarDay(nextStart, scheduledEndDate) &&
                        !dayjs(scheduledEndDate).isAfter(nextStart)
                      ) {
                        setScheduledEndDate(nextStart.add(2, 'hour'));
                      }
                    }}
                    ampm={false}
                    disabled={!scheduledDate}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        helperText: !scheduledDate ? 'Select start date first' : undefined
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TimePicker
                    label={isSameCalendarDay(scheduledDate, scheduledEndDate || scheduledDate) ? 'End time (same day)' : 'End time'}
                    value={scheduledEndDate}
                    onChange={(time) => {
                      const dateForEnd = scheduledEndDate || scheduledDate || dayjs();
                      const nextEnd = combineDateAndTime(dateForEnd, time, 8, 0);
                      setScheduledEndDate(nextEnd);
                    }}
                    ampm={false}
                    disabled={!scheduledDate}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        helperText: scheduledDate && scheduledEndDate && isSameCalendarDay(scheduledDate, scheduledEndDate)
                          ? 'Times apply on this day only'
                          : scheduledDate && scheduledEndDate
                            ? `Window runs until ${dayjs(scheduledEndDate).format('YYYY-MM-DD')}`
                            : (!scheduledDate ? 'Select start date first' : undefined)
                      }
                    }}
                  />
                </Grid>
                {scheduledDate && (scheduledEndDate || scheduledDate) ? (
                  <Grid item xs={12}>
                    <Alert severity="info" sx={{ py: 0.5 }}>
                      {isSameCalendarDay(scheduledDate, scheduledEndDate || scheduledDate)
                        ? `Same-day window: ${dayjs(scheduledDate).format('YYYY-MM-DD')} ${dayjs(scheduledDate).format('HH:mm')}–${dayjs(scheduledEndDate || scheduledDate).format('HH:mm')}`
                        : `Multi-day window: ${dayjs(scheduledDate).format('YYYY-MM-DD HH:mm')} → ${dayjs(scheduledEndDate).format('YYYY-MM-DD HH:mm')}`}
                    </Alert>
                  </Grid>
                ) : null}
              </>
            ) : (
              <Grid item xs={12}>
                <DatePicker
                  label={displayTimezone ? `Scheduled Date (${getTimezoneLabel(displayTimezone)})` : 'Scheduled Date (Planning/AM)'}
                  value={scheduledDate}
                  onChange={(date) => setScheduledDate(date)}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={needsStoreAction}
                    onChange={(e) => setNeedsStoreAction(e.target.checked)}
                  />
                }
                label="Needs Store Action"
              />
            </Grid>

            {requireHangarOperator && decision === 'APPROVE' && (
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel id="planning-operator-label">Operator</InputLabel>
                  <Select
                    labelId="planning-operator-label"
                    label="Operator"
                    value={operatorId}
                    onChange={(e) => setOperatorId(e.target.value)}
                  >
                    {operators.map((op) => (
                      <MenuItem key={op._id} value={op._id}>
                        {op.code ? `${op.code} — ${op.name}` : op.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Hangar external job: Planning assigns the Operator before the job enters Store/MRO.
                </Typography>
              </Grid>
            )}

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={needsMROAction}
                    onChange={(e) => setNeedsMROAction(e.target.checked)}
                  />
                }
                label="Needs MRO Action"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={needsExternalAgencyAction}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setNeedsExternalAgencyAction(checked);
                      if (!checked) setExternalAgencyId('');
                    }}
                  />
                }
                label="Needs External Agency Action"
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
                Outsource the work to an external organisation. Planning/CAMO upload CRS, CAME form, and other documents, then CAMO QM completion applies.
              </Typography>
            </Grid>

            {needsExternalAgencyAction && decision === 'APPROVE' && (
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel id="planning-external-agency-label">External Agency</InputLabel>
                  <Select
                    labelId="planning-external-agency-label"
                    label="External Agency"
                    value={externalAgencyId}
                    onChange={(e) => setExternalAgencyId(e.target.value)}
                  >
                    {externalAgencies.map((agency) => (
                      <MenuItem key={agency._id} value={agency._id}>
                        {agency.code ? `${agency.code} — ${agency.name}` : agency.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {externalAgencies.length === 0 && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    No active agencies with type <strong>External Maintenance Agency</strong> found.
                    Create one under Master Data → Agencies first.
                  </Alert>
                )}
              </Grid>
            )}

            {decision === 'APPROVE' && !needsStoreAction && !needsMROAction && !needsExternalAgencyAction && (
              <Grid item xs={12}>
                <Alert severity="warning" sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    <strong>Warning:</strong> At least one action (Store, MRO, or External Agency) must be selected for the work order to proceed through the workflow.
                  </Typography>
                </Alert>
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Notes (Optional)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any notes or comments..."
              />
            </Grid>
            {decision === 'REJECT' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  multiline
                  rows={3}
                  label="Rejection Remarks"
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  placeholder="Explain why this step is rejected and what must be corrected..."
                />
              </Grid>
            )}
          </Grid>
            </Box>
          )}

          {tab === 1 && (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                Provide parts procurement requirements that STORE will use to create parts procurement work order.
              </Alert>
              <Alert severity="info" sx={{ mb: 2 }}>
                Live store inventory is linked to part numbers entered below. Availability updates automatically.
                {inventoryAvailabilityLoading ? ' Refreshing inventory...' : ''}
              </Alert>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Box sx={{ mb: 2 }}>
                {partNumbers.map((partNumber, index) => (
                  <Grid container spacing={2} key={index} sx={{ mb: 2 }}>
                    <Grid item xs={3.5}>
                      <TextField
                        fullWidth
                        label={`Part Number ${index + 1}`}
                        value={partNumber}
                        onChange={(e) => handlePartNumberChange(index, e.target.value)}
                        placeholder="Enter part number"
                      />
                    </Grid>
                    <Grid item xs={2.5}>
                      <TextField
                        fullWidth
                        type="number"
                        label={`Quantity ${index + 1}`}
                        value={quantities[index] || ''}
                        onChange={(e) => handleQuantityChange(index, e.target.value)}
                        placeholder="Enter quantity"
                        inputProps={{ min: 1 }}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        label={`Description ${index + 1}`}
                        value={descriptions[index] || ''}
                        onChange={(e) => handleDescriptionChange(index, e.target.value)}
                        placeholder={partsLoading ? 'Loading part descriptions...' : 'Part description'}
                      />
                    </Grid>
                    <Grid item xs={2}>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', height: '100%' }}>
                        {partNumbers.length > 1 && (
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemovePartNumber(index)}
                          >
                            <Delete />
                          </IconButton>
                        )}
                        {index === partNumbers.length - 1 && (
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={handleAddPartNumber}
                          >
                            <Add />
                          </IconButton>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                ))}
              </Box>

              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<Download />}
                  onClick={handleDownloadProcurementCsv}
                  disabled={partNumbers.filter((pn) => pn.trim()).length === 0}
                >
                  Download CSV
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<UploadFile />}
                  onClick={handleUploadCsvClick}
                >
                  Upload CSV
                </Button>
                <input
                  type="file"
                  ref={csvUploadInputRef}
                  onChange={handleUploadProcurementCsv}
                  accept=".csv,text/csv"
                  style={{ display: 'none' }}
                />
              </Box>

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Specifications (Optional)"
                value={specifications}
                onChange={(e) => setSpecifications(e.target.value)}
                placeholder="Add specifications, notes, or requirements..."
                sx={{ mb: 2 }}
              />

              {procurementPreviewRows.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Remaining to Procure (Preview)
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Part Number</TableCell>
                          <TableCell align="right">Required</TableCell>
                          <TableCell align="right">Available</TableCell>
                          <TableCell align="right">Remaining</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {procurementPreviewRows.map((row) => (
                          <TableRow key={`${row.partNumber}-${row.requiredQty}`}>
                            <TableCell>{row.partNumber}</TableCell>
                            <TableCell align="right">{row.requiredQty}</TableCell>
                            <TableCell align="right">{row.availableQty}</TableCell>
                            <TableCell align="right">{row.remainingQty}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              <Button
                variant="contained"
                onClick={handleProvideProcurementRequirements}
                disabled={procurementLoading || partNumbers.filter(pn => pn.trim()).length === 0}
                fullWidth
              >
                {procurementLoading ? 'Providing Requirements...' : 'Provide Procurement Requirements'}
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading || procurementLoading}>
            Cancel
          </Button>
          {tab === 0 && (
            <Button 
              onClick={handleSubmit} 
              variant="contained" 
              color={decision === 'REJECT' ? 'error' : 'primary'}
              disabled={
                loading ||
                (decision === 'APPROVE'
                  ? (
                    !String(workOrder?.title || '').trim() ||
                    (!needsStoreAction && !needsMROAction && !needsExternalAgencyAction) ||
                    (needsExternalAgencyAction && !externalAgencyId) ||
                    (requireHangarOperator && !operatorId)
                  )
                  : !remarks.trim())
              }
            >
              {loading
                ? 'Submitting...'
                : (decision === 'REJECT' ? 'Reject' : approveLabel)}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default PlanningActions;

